/** Phase 8–10 — universal tri-language content resolution (Sanskrit + English + preferred). */

/** Indian languages supported for meaning layers. */
export const INDIAN_LANG_CODES = [
  "hi",
  "ta",
  "te",
  "ml",
  "kn",
  "mr",
  "gu",
  "bn",
  "or",
  "pa",
];

/** Global languages supported for meaning layers. */
export const GLOBAL_LANG_CODES = ["es", "fr", "de", "ar", "zh", "jp", "ru", "pt", "it"];

/** All selectable preferred languages (English = UI fallback, not shown as preferred layer). */
export const TRI_PREFERRED_LANG_CODES = [...INDIAN_LANG_CODES, ...GLOBAL_LANG_CODES];

/** @deprecated use TRI_PREFERRED_LANG_CODES */
export const CONTENT_LANG_CODES = INDIAN_LANG_CODES.slice(0, 5);

export const DEVANAGARI_RE = /[\u0900-\u097F]/;

const MEANING_FIELD_RE = /^(translation|meaning|summary|commentary|description)_([a-z]{2,3})$/;

export const LANG_LABELS = {
  en: "English",
  hi: "हिन्दी",
  ta: "தமிழ்",
  te: "తెలుగు",
  ml: "മലയാളം",
  kn: "ಕನ್ನಡ",
  mr: "मराठी",
  gu: "ગુજરાતી",
  bn: "বাংলা",
  or: "ଓଡ଼ିଆ",
  pa: "ਪੰਜਾਬੀ",
  es: "Español",
  fr: "Français",
  de: "Deutsch",
  ar: "العربية",
  zh: "中文",
  jp: "日本語",
  ru: "Русский",
  pt: "Português",
  it: "Italiano",
  sa: "संस्कृतम्",
};

export const TRI_PREFERRED_LANG_GROUPS = [
  { id: "indian", label: "Indian languages", codes: INDIAN_LANG_CODES },
  { id: "global", label: "Global languages", codes: GLOBAL_LANG_CODES },
];

export const DEFAULT_TRI_SETTINGS = {
  preferredLanguage: "en",
  showEnglish: true,
  showSanskrit: true,
};

const STORAGE_KEY = "vedapath_tri_lang";

/** Normalize user-facing language codes (incl. browser ja → jp). */
export function normalizePreferredLang(code) {
  if (!code) return "en";
  let base = String(code).split("-")[0].toLowerCase();
  if (base === "ja") base = "jp";
  if (base === "en" || TRI_PREFERRED_LANG_CODES.includes(base)) return base;
  return "en";
}

export function getLanguageLabel(code) {
  const c = normalizePreferredLang(code);
  return LANG_LABELS[c] || c.toUpperCase();
}

export function loadTriSettings() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_TRI_SETTINGS };
    const parsed = JSON.parse(raw);
    return {
      ...DEFAULT_TRI_SETTINGS,
      ...parsed,
      preferredLanguage: normalizePreferredLang(parsed.preferredLanguage),
    };
  } catch {
    return { ...DEFAULT_TRI_SETTINGS };
  }
}

export function saveTriSettings(settings) {
  const merged = {
    ...loadTriSettings(),
    ...settings,
    preferredLanguage: settings.preferredLanguage
      ? normalizePreferredLang(settings.preferredLanguage)
      : loadTriSettings().preferredLanguage,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  return merged;
}

function isMeaningText(value) {
  return typeof value === "string" && value.trim() && !DEVANAGARI_RE.test(value);
}

/**
 * Discover all meaning/translation language codes present on an item.
 */
export function discoverTranslationCodes(item, prefixes = ["translation", "meaning", "summary", "commentary"]) {
  if (!item || typeof item !== "object") return [];
  const codes = new Set();
  for (const [key] of Object.entries(item)) {
    const m = key.match(MEANING_FIELD_RE);
    if (!m) continue;
    if (prefixes.length && !prefixes.includes(m[1])) continue;
    codes.add(m[2]);
  }
  return [...codes].sort();
}

/** Collect all translation/meaning/summary fields from a content item. */
export function extractMeaningFields(item) {
  if (!item || typeof item !== "object") return {};
  const out = {};
  for (const [key, value] of Object.entries(item)) {
    if (typeof value !== "string" || !value.trim()) continue;
    if (MEANING_FIELD_RE.test(key)) out[key] = value.trim();
  }
  return out;
}

/**
 * True when item carries Sanskrit script or IAST/transliteration.
 */
export function hasSanskritContent(item) {
  if (item == null || item === "") return false;
  if (typeof item === "string") return DEVANAGARI_RE.test(item);
  const candidates = [
    item.sanskrit_devanagari,
    item.sanskrit,
    item.sanskrit_text,
    item.text,
    item.mantra,
    item.passage,
  ];
  if (candidates.some((f) => DEVANAGARI_RE.test(String(f || "")))) return true;
  return Boolean(item.sanskrit_iast || item.transliteration || item.iast);
}

/**
 * Resolve English text from item (supports multiple field naming conventions).
 */
export function pickEnglish(item, prefix = "translation") {
  if (!item) return "";
  const candidates = [
    item[`${prefix}_en`],
    item.translation_en,
    item.meaning_en,
    item.summary_en,
    item.description_en,
    item.commentary_en,
    item.english,
    item.summary,
    item.meaning,
    item.description,
    item.text_en,
    item.name,
  ];
  for (const c of candidates) {
    if (isMeaningText(c)) return c.trim();
  }
  return "";
}

/**
 * Resolve preferred-language text with English fallback.
 * Auto-detects ANY translation_* / meaning_* / summary_* field on the item.
 */
export function pickPreferred(item, lang, prefix = "translation") {
  if (!item || !lang || lang === "en") return pickEnglish(item, prefix);
  const code = normalizePreferredLang(lang);
  const prefixOrder = [...new Set([prefix, "translation", "meaning", "summary", "commentary", "description"])];

  for (const p of prefixOrder) {
    const direct = item[`${p}_${code}`];
    if (typeof direct === "string" && direct.trim()) return direct.trim();
  }

  for (const [key, value] of Object.entries(item)) {
    if (typeof value !== "string" || !value.trim()) continue;
    const m = key.match(MEANING_FIELD_RE);
    if (m && m[2] === code) return value.trim();
  }

  return pickEnglish(item, prefix);
}

/**
 * Normalize legacy / API shapes into tri-language fields.
 */
export function normalizeTriLanguageItem(item) {
  if (typeof item === "string") {
    const s = item.trim();
    if (DEVANAGARI_RE.test(s)) {
      return { sanskrit_devanagari: s, translation_en: "" };
    }
    return { translation_en: s };
  }
  if (!item || typeof item !== "object") return {};

  const rawText = item.text || item.mantra || item.passage || "";
  const deva =
    item.sanskrit_devanagari ||
    item.sanskrit ||
    item.sanskrit_text ||
    (DEVANAGARI_RE.test(String(rawText)) ? String(rawText) : "");

  return {
    ...extractMeaningFields(item),
    ...item,
    sanskrit_devanagari: deva,
    sanskrit_iast: item.sanskrit_iast || item.transliteration || item.iast || "",
    translation_en:
      pickEnglish(item) || pickEnglish(item, "meaning") || pickEnglish(item, "summary"),
  };
}

/** Build tri item from loose field names (used across App.js modules). */
export function toTriFromFields({
  sanskrit,
  iast,
  transliteration,
  english,
  summary,
  meaning,
  ...rest
} = {}) {
  return normalizeTriLanguageItem({
    sanskrit_devanagari: sanskrit,
    sanskrit_iast: iast || transliteration,
    translation_en: english || summary || meaning,
    ...rest,
  });
}

export function mantraToTriItem(m) {
  if (!m) return {};
  if (typeof m === "string") return normalizeTriLanguageItem({ text: m });
  return normalizeTriLanguageItem({
    sanskrit_devanagari: m.text || m.sanskrit,
    sanskrit_iast: m.transliteration,
    translation_en: m.meaning || m.translation_en,
    ...m,
  });
}

export function verseCanonicalToTri(verse) {
  const c = verse?.canonical || verse;
  return normalizeTriLanguageItem({
    sanskrit_devanagari: c?.sanskrit,
    sanskrit_iast: c?.transliteration,
    translation_en: c?.english_meaning,
    ...verse,
  });
}

/**
 * Normalize any content item into tri-language shape for rendering.
 */
export function resolveTriLanguage(item, preferredLang = "en", options = {}) {
  const { meaningPrefix = "translation" } = options;
  const lang = preferredLang === "sa" ? "hi" : normalizePreferredLang(preferredLang);
  const normalized = normalizeTriLanguageItem(item);

  const sanskrit_devanagari =
    normalized.sanskrit_devanagari || normalized.sanskrit || normalized.sanskrit_text || "";
  const sanskrit_iast =
    normalized.sanskrit_iast || normalized.transliteration || normalized.iast || "";

  let english = pickEnglish(normalized, meaningPrefix).trim();
  let preferred = pickPreferred(normalized, lang, meaningPrefix).trim();

  if (!preferred) preferred = english;
  if (!english && preferred) english = preferred;
  if (!english && sanskrit_iast.trim()) english = sanskrit_iast.trim();
  if (!english && sanskrit_devanagari.trim()) english = sanskrit_devanagari.trim();

  const deva = sanskrit_devanagari.trim();
  const iast = sanskrit_iast.trim();

  return {
    sanskrit_devanagari: deva,
    sanskrit_iast: iast,
    english,
    preferred: preferred || english,
    preferredLang: lang,
    hasPreferred: Boolean(preferred && english && preferred !== english),
    hasSanskrit: Boolean(deva || iast),
    availableTranslationCodes: discoverTranslationCodes(normalized),
  };
}

/** Phase AV — English meaning text for AI voice (never Sanskrit-only). */
export function getEnglishMeaning(item, meaningPrefix = "translation") {
  const normalized = normalizeTriLanguageItem(item);
  const text = pickEnglish(normalized, meaningPrefix).trim();
  if (text) return text;
  return resolveTriLanguage(normalized, "en", { meaningPrefix }).english.trim();
}

/** Phase AV — preferred-language meaning with English fallback for AI voice. */
export function getPreferredMeaning(item, preferredLang = "en", meaningPrefix = "translation") {
  const lang = normalizePreferredLang(preferredLang);
  const normalized = normalizeTriLanguageItem(item);
  const english = getEnglishMeaning(normalized, meaningPrefix);
  if (!lang || lang === "en") return english;

  const preferred = pickPreferred(normalized, lang, meaningPrefix).trim();
  if (preferred && preferred !== english) return preferred;
  return english;
}

export function translationFieldForLang(lang) {
  const code = normalizePreferredLang(lang);
  if (!code || code === "en") return "translation_en";
  return `translation_${code}`;
}

/** Map chant slide / API payload to tri-language item (all translation_* fields preserved). */
export function mapSlideToTriItem(slide) {
  if (!slide) return {};
  return normalizeTriLanguageItem({
    sanskrit_devanagari: slide.sanskrit,
    sanskrit_iast: slide.transliteration || slide.sanskrit_iast,
    meaning_en: slide.meaning_en || slide.meaning,
    translation_en: slide.translation_en || slide.meaning_en || slide.meaning,
    commentary_en: slide.commentary_en || slide.commentary,
    ...extractMeaningFields(slide),
    ...slide,
  });
}

/** Phase CL — CMS / API story → tri-language item (meaningPrefix="summary"). */
export function storyToTriItem(story) {
  if (!story) return {};
  const c = story.canonical || story;
  return normalizeTriLanguageItem({
    sanskrit_devanagari: c.sanskrit_devanagari || c.sanskrit,
    sanskrit_iast: c.sanskrit_iast || c.transliteration,
    summary_en: c.summary_en || c.short_story || c.full_story || c.summary || story.summary_en || story.summary,
    moral_en: c.moral,
    ...extractMeaningFields(c),
    ...extractMeaningFields(story),
    ...c,
    ...story,
  });
}

/** Phase CL — guru profile → tri-language item (meaningPrefix="translation"). */
export function guruToTriItem(guru) {
  if (!guru) return {};
  const c = guru.canonical || guru;
  return normalizeTriLanguageItem({
    sanskrit_devanagari: c.sanskrit_devanagari || c.name_sanskrit || c.sanskrit,
    sanskrit_iast: c.sanskrit_iast,
    translation_en: c.translation_en || c.short_intro || c.short_teaching || guru.short_teaching,
    name_en: c.name || c.title,
    ...extractMeaningFields(c),
    ...extractMeaningFields(guru),
    ...c,
    ...guru,
  });
}

/** Phase CL — yoga āsana → tri-language item (meaningPrefix="description"). */
export function asanaToTriItem(asana) {
  if (!asana) return {};
  const benefits = Array.isArray(asana.benefits_for_sadhana) ? asana.benefits_for_sadhana.join(". ") : "";
  return normalizeTriLanguageItem({
    sanskrit_devanagari: asana.name_sanskrit || asana.sanskrit_devanagari,
    sanskrit_iast: asana.sanskrit_iast,
    description_en: asana.description_en || asana.instructions_en || benefits || asana.name_common,
    ...extractMeaningFields(asana),
    ...asana,
  });
}

/** Phase CL — sādhana step → tri-language item (meaningPrefix="meaning"). */
export function sadhanaStepToTriItem(step) {
  if (!step) return {};
  if (typeof step === "string") return normalizeTriLanguageItem({ meaning_en: step, text: step });
  return normalizeTriLanguageItem({
    sanskrit_devanagari: step.sanskrit_devanagari || step.sanskrit,
    sanskrit_iast: step.sanskrit_iast || step.transliteration,
    meaning_en: step.meaning_en || step.instruction_en || step.text_en || step.text,
    ...extractMeaningFields(step),
    ...step,
  });
}
